/**
 * Collapses per-row warnings ("Skipped row 12: no account code …") that share
 * the same reason into one entry, so a file with hundreds of skipped rows
 * yields one line instead of hundreds.
 */

export interface TWarningGroup {
    /** Display text: the original message, or a "<prefix>N rows: <reason>" summary. */
    message: string;
    /** Row numbers in file order; empty for messages that do not reference a row. */
    rows: number[];
}

// "Skipped row 12: reason" / "Row 5: reason" — the reason is the grouping key.
const ROW_MESSAGE = /^(.*?)\brow\s+(\d+):\s*(.*)$/i;

function capitalize(text: string): string {
    return text.charAt(0).toUpperCase() + text.slice(1);
}

interface TPendingGroup {
    original: string;
    prefix: string;
    reason: string;
    rows: number[];
}

export function groupWarnings(messages: string[]): TWarningGroup[] {
    const groups: TPendingGroup[] = [];
    const byKey = new Map<string, TPendingGroup>();

    for (const message of messages) {
        const match = ROW_MESSAGE.exec(message);
        if (!match) {
            groups.push({ original: message, prefix: "", reason: "", rows: [] });
            continue;
        }
        const [, prefix = "", row = "", reason = ""] = match;
        const key = `${prefix.toLowerCase()}\u0000${reason}`;
        const existing = byKey.get(key);
        if (existing) {
            existing.rows.push(Number(row));
            continue;
        }
        const group = { original: message, prefix, reason, rows: [Number(row)] };
        byKey.set(key, group);
        groups.push(group);
    }

    return groups.map(({ original, prefix, reason, rows }) => ({
        message: rows.length > 1 ? capitalize(`${prefix}${rows.length} rows: ${reason}`) : original,
        rows: rows.length > 1 ? rows : [],
    }));
}

/** Formats row numbers compactly: [1, 2, 3, 5, 7, 8] → ["1–3", "5", "7–8"]. */
export function formatRowRanges(rows: number[]): string[] {
    const sorted = [...new Set(rows)].sort((a, b) => a - b);
    const ranges: string[] = [];
    const push = (start: number, end: number) =>
        ranges.push(start === end ? `${start}` : `${start}–${end}`);

    let start: number | undefined;
    let prev = 0;
    for (const row of sorted) {
        if (start === undefined) {
            start = row;
        } else if (row !== prev + 1) {
            push(start, prev);
            start = row;
        }
        prev = row;
    }
    if (start !== undefined) push(start, prev);
    return ranges;
}
